import React from 'react';
import PropTypes from 'prop-types';
import classnames from 'classnames';
import Heading from '@deliveroo/consumer-component-library/components/Heading';
import P from '@deliveroo/consumer-component-library/components/P';
import Spacer from '@deliveroo/consumer-component-library/components/Spacer';

import HomeFiltersFulfillmentControl from '../../containers/HomeFiltersFulfillmentControl';
import HomeFiltersLogisticsControl from '../HomeFiltersLogisticsControl';
import HomeFiltersSection from '../HomeFiltersSection';

import withPlaceholder from '../withPlaceholder';
import i18n from '../../utils/i18n';
import { fulfillmentMethodType } from '../../containers/HomeFiltersFulfillmentControl/proptypes';
import Filters from './filters';

import styles from './styles.scss';

const placeHolderRows = (placeholderStyles, rowCount = 1) =>
  Array.from({ length: rowCount }, (_, i) => (
    <div className={`${placeholderStyles} ${styles.placeholderRow}`} key={i} />
  ));

class SidebarFiltersHome extends React.Component {
  constructor(props) {
    super(props);

    this.state = {
      isScrolled: false,
    };
  }

  componentDidMount() {
    if (this.scrollSectionEl) {
      this.scrollSectionEl.addEventListener('scroll', this.onScroll);
    }
  }

  componentWillUnmount() {
    if (this.scrollSectionEl) {
      this.scrollSectionEl.removeEventListener('scroll', this.onScroll);
    }
  }

  onScroll = () => {
    const SCROLL_OFFSET = 10;
    if (!this.state.isScrolled && this.scrollSectionEl.scrollTop > SCROLL_OFFSET) {
      this.setState({
        isScrolled: true,
      });
    }

    if (this.state.isScrolled && this.scrollSectionEl.scrollTop < SCROLL_OFFSET) {
      this.setState({
        isScrolled: false,
      });
    }
  };

  render() {
    const {
      filters,
      setSelectedFilters,
      selectedFilters,
      isPlaceholder,
      placeholderClass,
      neighborhood,
      dateTimeLabel,
      trackToggleFilterSection,
      withClickAndCollect,
      showFulfillmentSwitchControl,
      showLogisticsModal,
      toggleLogisticsModal,
      trackTappedLogisticsControl,
      selectedMethod,
    } = this.props;

    const scrollSectionStyles = classnames(styles['scroll-section'], {
      [styles.isScrolled]: this.state.isScrolled,
    });

    const desktopLogisticsControl = (
      <Spacer spacing="0 medium 0 0">
        <HomeFiltersSection>
          <HomeFiltersLogisticsControl
            dateTimeLabel={dateTimeLabel}
            neighborhood={neighborhood}
            showLogisticsModal={showLogisticsModal}
            toggleLogisticsModal={toggleLogisticsModal}
            trackTappedLogisticsControl={trackTappedLogisticsControl}
            selectedMethod={selectedMethod}
            withClickAndCollect={withClickAndCollect}
          />
        </HomeFiltersSection>
        {withClickAndCollect &&
          showFulfillmentSwitchControl && (
            <HomeFiltersSection>
              <Spacer spacing="medium 0 0 0">
                <HomeFiltersFulfillmentControl inputName="sidebar-filters__fulfillment-method" />
              </Spacer>
            </HomeFiltersSection>
          )}
      </Spacer>
    );

    return (
      <div className={styles['sidebar-wrapper']}>
        {desktopLogisticsControl}
        {isPlaceholder &&
          !filters.length && (
            <React.Fragment>
              <div className={styles['filter-section']}>{placeHolderRows(placeholderClass, 3)}</div>
              <div className={styles['filter-section']}>{placeHolderRows(placeholderClass, 3)}</div>
            </React.Fragment>
          )}
        <div
          className={scrollSectionStyles}
          ref={(el) => {
            this.scrollSectionEl = el;
          }}
        >
          {!isPlaceholder &&
            !filters.length && (
              <div className={styles.emptyFilters}>
                <Heading level={4} size="x-small" spacing="small 0">
                  {i18n.gettext('home.filters.no_filters.heading')}
                </Heading>
                <P color="grey">{i18n.gettext('home.filters.no_filters.message')}</P>
              </div>
            )}
          {Boolean(filters.length) && (
            <Filters
              filters={filters}
              setSelectedFilters={setSelectedFilters}
              selectedFilters={selectedFilters}
              trackToggleFilterSection={trackToggleFilterSection}
            />
          )}
        </div>
      </div>
    );
  }
}

SidebarFiltersHome.propTypes = {
  /** Array of filters to render in the sidebar */
  filters: PropTypes.arrayOf(
    PropTypes.shape({
      id: PropTypes.string.isRequired,
      header: PropTypes.string.isRequired,
      images: PropTypes.shape({
        icon: PropTypes.shape({
          name: PropTypes.string,
          image: PropTypes.string,
        }),
      }).isRequired,
      options: PropTypes.arrayOf(
        PropTypes.shape({
          count: PropTypes.number,
          name: PropTypes.string.isRequired,
          selected: PropTypes.bool,
          id: PropTypes.oneOfType([PropTypes.string, PropTypes.number]).isRequired,
        }).isRequired,
      ).isRequired,
      styling: PropTypes.shape({
        mobile: PropTypes.shape({
          collapse: PropTypes.string,
          seperators: PropTypes.string,
        }),
        desktop: PropTypes.shape({
          collapse: PropTypes.string,
          seperators: PropTypes.string,
        }),
      }),
      optionsType: PropTypes.string.isRequired,
    }),
  ),

  /** Array of filters that have been selected */
  selectedFilters: PropTypes.arrayOf(PropTypes.string).isRequired,

  /** Callback to handle clicking on filters */
  setSelectedFilters: PropTypes.func.isRequired,

  /** Current users location */
  neighborhood: PropTypes.string,

  /** Tells withPlaceholder component to render placehold style (hooked up to loading state) */
  isPlaceholder: PropTypes.bool,

  /** Render the new UI components relaed to the Click and Collect feature */
  withClickAndCollect: PropTypes.bool.isRequired,

  /**
   * Whether the switch control should be rendered or not.
   */
  showFulfillmentSwitchControl: PropTypes.bool.isRequired,

  /** Render the Logistics Control modal opened or not */
  showLogisticsModal: PropTypes.bool.isRequired,

  /** Toggles the open/close state of the Logistics Control modal */
  toggleLogisticsModal: PropTypes.func.isRequired,

  /** Fire tracking event when opening the logistics control modal */
  trackTappedLogisticsControl: PropTypes.func.isRequired,

  /** Class applied bt whitPlaceholder component for loading state */
  placeholderClass: PropTypes.string,

  /** String to show the date time selected */
  dateTimeLabel: PropTypes.string,

  /** Method that will fire tracking event when state of collapsible section is changed * */
  trackToggleFilterSection: PropTypes.func.isRequired,

  /**
   * The currently selected fulfillment method.
   */
  selectedMethod: fulfillmentMethodType.isRequired,
};

SidebarFiltersHome.defaultProps = {
  isPlaceholder: false,
  placeholderClass: undefined,
  neighborhood: '',
  dateTimeLabel: '',
  filters: [],
};

export default withPlaceholder(SidebarFiltersHome, {});
